import { EventEntry } from "../../../models";
import { ExpandedCardStates } from "./newEntryTypes";

/**
 * Horse / rider title strings for compact cards, pending-save subtitles, and the unsaved-changes preview.
 * Pending values use `"Horse & Rider"` (and optional second rider) so the UI can update before the DB round-trip.
 */

/** Split a saved entry (or a pending `"Horse & Rider"` string) into the two compact-card title lines. */
export const getEntryHeaderLines = (entry: EventEntry, pendingValue?: string): { horseLine: string; ridersLine: string } => {
    if (pendingValue && pendingValue.includes(" & ")) {
        const [horsePart, ...riderParts] = pendingValue.split(" & ");
        const horseLine = horsePart?.trim() || entry.horseName?.trim() || "Horse";
        const ridersLine = riderParts.join(" & ").trim();
        return { horseLine, ridersLine: ridersLine || entry.riderName?.trim() || "Rider" };
    }

    const horseLine = entry.horseName?.trim() || "Horse";
    const riderOne = entry.riderName?.trim() || "Rider";
    const riderTwo = entry.secondRiderName?.trim() || entry.secondRider?.name?.trim();
    return { horseLine, ridersLine: riderTwo ? `${riderOne} & ${riderTwo}` : riderOne };
};

/** Horse name only from a pending `"Horse & Rider"` subtitle; drafts default to "New Horse". */
export const getPendingHorseName = (pendingValue?: string): string => {
    return (pendingValue?.split(" & ")?.[0] || "New Horse").trim();
};

/** Build the pending subtitle stored in the map while create/update is in flight. */
export const formatPendingEntrySubtitle = (horseName?: string, riderName?: string, secondRiderName?: string): string => {
    const horse = horseName || "Horse";
    const rider = riderName || "Rider";
    return `${horse} & ${rider}${secondRiderName ? " & " + secondRiderName : ""}`;
};

/** Icon + title + subtitle shown on the unsaved-changes modal for the currently expanded section. */
export const getExpandedSectionInfo = (
    expandedCard: ExpandedCardStates,
    currentEntries: EventEntry[] | undefined | null,
    pendingEntrySubtitles: Map<string | null, string>
): { icon: string; title: string; subtitle?: string } => {
    if (expandedCard === "stabling") {
        return { icon: "📦", title: "Stabling + Extras", subtitle: "Stalls, Bedding, Feed + Extras" };
    }
    if (expandedCard === "waivers") {
        return { icon: "📋", title: "Waivers", subtitle: "Rules, Agreements, Waivers" };
    }
    if (expandedCard === "entry") {
        return {
            icon: "🐴",
            title: getPendingHorseName(pendingEntrySubtitles.get(null)),
            subtitle: `Entry ${(currentEntries ? currentEntries.length : 0) + 1}`,
        };
    }
    if (expandedCard?.startsWith("entry")) {
        const entryIndex = Number.parseInt(expandedCard.replace("entry", ""), 10) - 1;
        const entry = currentEntries?.[entryIndex];
        if (entry) {
            const pendingHeader = pendingEntrySubtitles.get(entry.id || null);
            const { horseLine, ridersLine } = getEntryHeaderLines(entry, pendingHeader);
            return { icon: "🐴", title: horseLine, subtitle: ridersLine };
        }
    }
    return { icon: "💾", title: "Open Section" };
};
